import type { CompanyCandidate } from './api';

const STORAGE_KEY = 'osservio:local-activity-companies';
const MAX_ROWS = 100;

export interface RememberedActivityCompany {
  activityKey: string;
  activityName: string;
  address: string | null;
  vat: string;
  companyName: string;
  candidate: CompanyCandidate | null;
  updatedAt: string;
}

function normalize(value: string | null | undefined) {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function activityKey(name: string, address?: string | null) {
  return `${normalize(name)}|${normalize(address)}`;
}

function isCompanyCandidate(value: unknown): value is CompanyCandidate {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return typeof row.vat === 'string' && typeof row.companyName === 'string';
}

function isRememberedCompany(value: unknown): value is RememberedActivityCompany {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return typeof row.activityKey === 'string'
    && typeof row.activityName === 'string'
    && typeof row.vat === 'string'
    && typeof row.companyName === 'string'
    && (row.candidate == null || isCompanyCandidate(row.candidate));
}

export function readRememberedActivityCompanies(): RememberedActivityCompany[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isRememberedCompany) : [];
  } catch {
    return [];
  }
}

export function readRememberedActivityCompany(name: string, address?: string | null) {
  const exactKey = activityKey(name, address);
  const rows = readRememberedActivityCompanies();
  return rows.find((row) => row.activityKey === exactKey)
    ?? rows.find((row) => normalize(row.activityName) === normalize(name))
    ?? null;
}

export function rememberActivityCompany(input: {
  activityName: string;
  address?: string | null;
  vat: string;
  companyName?: string | null;
  candidate?: CompanyCandidate | null;
}) {
  const vat = input.vat.replace(/\s+/g, '').toUpperCase();
  const key = activityKey(input.activityName, input.address);
  if (!normalize(input.activityName) || !vat) return;
  const row: RememberedActivityCompany = {
    activityKey: key,
    activityName: input.activityName.trim(),
    address: input.address?.trim() || null,
    vat,
    companyName: input.companyName?.trim() || input.candidate?.companyName || `P.IVA ${vat}`,
    candidate: input.candidate ?? null,
    updatedAt: new Date().toISOString(),
  };
  const previous = readRememberedActivityCompanies().filter((item) => item.activityKey !== key);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([row, ...previous].slice(0, MAX_ROWS)));
  } catch {
    // La memoria migliora i ritorni successivi, ma non deve mai bloccare il flusso corrente.
  }
}

export function forgetActivityCompany(name: string, address?: string | null) {
  const key = activityKey(name, address);
  const next = readRememberedActivityCompanies().filter((row) => row.activityKey !== key);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // In modalità privata o con storage pieno lo stato React corrente resta valido.
  }
}

export function rememberedCompanyNameForVat(vat: string) {
  const normalizedVat = vat.replace(/\s+/g, '').toUpperCase();
  return readRememberedActivityCompanies().find((row) => row.vat === normalizedVat)?.companyName ?? null;
}
